import { useAtom } from "jotai/index";
import React from "react";

import { isNewActivityOpenAtom } from "@/Pages/Reporting/Owner/Index";
import { Dialog, Formheader } from "@/Components";
import { BarChart2, CheckCircle, PieChart, Type } from "react-feather";
import { createQuestionAtom } from "@/Stores";
import { Inertia } from "@inertiajs/inertia";
import { QUESTION_TYPES } from "@/Enums/question_types";
import { usePage } from "@inertiajs/inertia-react";
import { PropTypes } from "prop-types";

function NewActivityChooseType({ source = "owner" }) {
  const [openModal, setOpenModal] = useAtom(isNewActivityOpenAtom);
  const [payload, setPayload] = useAtom(createQuestionAtom);

  const team = usePage().props?.team?.data || {};

  if (!openModal) return null;

  const handleFormUpdate = (e) => {
    const { name, value } = e.target;
    setPayload({ ...payload, [name]: value });
  };

  const questionTypes = [
    {
      value: QUESTION_TYPES.toInt(QUESTION_TYPES.Calculate),
      label: QUESTION_TYPES.toString(QUESTION_TYPES.Calculate),
      description:
        "Report on usage or spendage for an activity to calculate emissions",
      icon: (
        <BarChart2 className="size-10 rounded-full bg-gray p-2 text-gray-inactive peer-checked:bg-primary peer-checked:text-white" />
      ),
    },
    {
      value: QUESTION_TYPES.toInt(QUESTION_TYPES.Select),
      label: QUESTION_TYPES.toString(QUESTION_TYPES.Select),
      description: "Give the reporter a choice between different answers.",
      icon: (
        <CheckCircle className="size-10 rounded-full bg-gray p-2 text-gray-inactive peer-checked:bg-primary peer-checked:text-white" />
      ),
    },
    {
      value: QUESTION_TYPES.toInt(QUESTION_TYPES.FreeText),
      label: QUESTION_TYPES.toString(QUESTION_TYPES.FreeText),
      description: "Get free text answers from your reporters.",
      icon: (
        <Type className="size-10 rounded-full bg-gray p-2 text-gray-inactive peer-checked:bg-primary peer-checked:text-white" />
      ),
    },
    {
      value: QUESTION_TYPES.toInt(QUESTION_TYPES.Distribute),
      label: QUESTION_TYPES.toString(QUESTION_TYPES.Distribute),
      description: "Distribute impact of two or more different entities.",
      icon: (
        <PieChart className="size-10 rounded-full bg-gray p-2 text-gray-inactive peer-checked:bg-primary peer-checked:text-white" />
      ),
    },
  ];

  return (
    <Dialog
      closeModal={() => setOpenModal(false)}
      openModal={openModal}
      title="Choose type of activity"
      modalStyle="popup"
      animation="fade-in"
      className="lg:ml-[304px] lg:mr-[64px] 4xl:ml-[364px] 4xl:mr-[64px]"
    >
      <div className="w-full">
        <div className="mb-8 mt-4 flex w-full flex-col gap-4 md:flex-row">
          {questionTypes.map((type) => (
            <label
              htmlFor={`question_type_${type.value}`}
              key={type.value}
              className="shadow-small flex-1 cursor-pointer border border-borderColor p-8 has-[:checked]:border-primary has-[:checked]:bg-primary-light"
            >
              <div className="flex flex-row-reverse justify-between">
                <input
                  type="radio"
                  name="question_type_id"
                  id={`question_type_${type.value}`}
                  value={type.value}
                  className="peer"
                  onChange={handleFormUpdate}
                />
                {type.icon}
              </div>
              <Formheader>{type.label}</Formheader>
              <p>{type.description}</p>
            </label>
          ))}
        </div>

        <div className="flex justify-end gap-4">
          <button
            onClick={() => setOpenModal(false)}
            className="text-blue underline hover:no-underline"
          >
            Never mind
          </button>
          <button
            className="btn btn-blue grow-0 px-4 py-3"
            disabled={!payload?.question_type_id}
            onClick={() => {
              setOpenModal(false);

              switch (source) {
                case "owner":
                  Inertia.visit(
                    route("question.create", [
                      team.id,
                      payload.question_type_id,
                    ]),
                  );
                  break;
                case "library":
                  Inertia.visit(
                    route("library.activity.create", payload.question_type_id),
                  );
                  break;
                case "agency":
                  Inertia.visit(
                    route("agency.question.create", payload.question_type_id),
                  );
                  break;
                case "admin":
                  Inertia.visit(
                    route("admin.question.create", payload.question_type_id),
                  );
                  break;
              }
            }}
          >
            Create new activity
          </button>
        </div>
      </div>
    </Dialog>
  );
}

NewActivityChooseType.propTypes = {
  source: PropTypes.oneOf(["owner", "library", "admin", "agency"]).isRequired,
};

export default NewActivityChooseType;
